import styles from './SalesContactModule.scss';

import Grid, { GridDirection } from '@material-ui/core/Grid';
import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import React, { FC, HTMLAttributes, useRef } from 'react';

import { ImageAlignment, ImageMobileAlignment, SalesContactModule as SalesContactModuleType } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ButtonLinkV2 } from 'src/components/Button/ButtonLinkV2';
import { ContentImage } from 'src/components/ContentImage/ContentImage';
import { ContentLinkV2 } from 'src/components/ContentLinkV2/ContentLinkV2';
import { GridContainer } from 'src/components/GridContainer/GridContainer';
import { getReferralType } from 'src/components/SalesChannel/Util';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useDesktopOrLarge } from 'src/hooks/UseResponsive';
import { FlatEntry, flattenContentfulData } from 'src/services/cms/flattenContentful';

export interface SalesContactModuleProps extends HTMLAttributes<HTMLElement> {
  entry?: FlatEntry<SalesContactModuleType>;
}

const SalesContactModule: FC<SalesContactModuleProps> = ({
  entry,
  style = {},
  className,
  ...props
}) => {
  const isDesktopOrLarge = useDesktopOrLarge();
  const { current: isReferralTypeBC } = useRef(getReferralType() === 'BC');

  if (!entry) return null;
  const {
    backgroundColor,
    image,
    imageAlignment,
    imageMobileAlignment,
    phoneLink,
    contactLink,
    titleColor,
    blurbColor,
    title,
    blurb,
    phoneTitle,
  } = entry;

  const direction = getFlexDirection(imageAlignment, imageMobileAlignment, isDesktopOrLarge);

  return (
    <section
      className={cx(styles.salesContactModule, className)}
      style={{ ...style, backgroundColor: backgroundColor || style.backgroundColor }}
      {...props}
    >
      <GridContainer direction={direction}>
        <Grid container item xs={12} lg={6} xl={5} justifyContent="center">
          <Grid item xs={12} md={10} lg={8} className={styles.salesContactModuleContentGrid}>
            <div className={styles.salesContactModuleContent}>
              <TextV2 isMarkdown value={title} entry={{ ...entry, field: 'title' }} tagName="h2" variant="h3" className="title" style={{ color: titleColor }} />
              <TextV2 isMarkdown value={blurb} entry={{ ...entry, field: 'blurb' }} tagName="div" variant="body" className={styles.salesContactModuleBlurb} style={{ color: blurbColor }} />
              {!isReferralTypeBC && (
                <>
                  <TextV2 isMarkdown value={phoneTitle} entry={{ ...entry, field: 'phoneTitle' }} tagName="h3" variant="body" className={styles.salesContactModulePhoneTitle} />
                  <ContentLinkV2 entry={phoneLink} variant="h4-bold" className={styles.salesContactModulePhoneNumber} />
                </>
              )}
              <ButtonLinkV2 entry={contactLink} className={styles.salesContactModuleScheduleButton} />
            </div>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={6} xl={7}>
          <div className={styles.salesContactModuleImageContainer}>
            <ContentImage entry={image} className={styles.salesContactModuleImage} />
          </div>
        </Grid>
      </GridContainer>
    </section>
  );
};

SalesContactModule.displayName = 'SalesContactModule';

const getFlexDirection = (
  imageAlignment?: ImageAlignment,
  imageMobileAlignment?: ImageMobileAlignment,
  isDesktopOrLarge?: boolean
): GridDirection => {
  if (isDesktopOrLarge && imageAlignment === ImageAlignment.LEFT) return 'row-reverse';
  else if (!isDesktopOrLarge && imageMobileAlignment === ImageMobileAlignment.TOP) return 'column-reverse';
  return 'row';
};

interface SalesContactModuleWrapperProps extends HTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<SalesContactModuleType>;
}

const SalesContactModuleWrapper: FC<SalesContactModuleWrapperProps> = ({ entry, ...props }) =>
  React.createElement(SalesContactModule, { ...props, entry: flattenContentfulData(entry) });

SalesContactModuleWrapper.displayName = 'SalesContactModuleWrapper';

export {
  SalesContactModule as _SalesContactModule,
  SalesContactModuleWrapper as SalesContactModule,
};
